import { useState } from "react";
import { ExternalLink, ThumbsDown, ThumbsUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { RequirementEligibility } from "@api/services/job-post/pure-requirements-evaluator";
import { JobShareButton } from "../../components/job-share-button";
import { JobPostExternalAcceptDialog } from "../../job-post-external-accept-dialog";

type JobPostProviderActionsProps = {
  job: {
    id?: string;
    title?: string;
    externalUrl?: string | null;
    eligibility?: RequirementEligibility;
    /** The provider's saved decision; lists of undecided jobs omit it. */
    decision?: { interested: boolean } | null;
  };
  facilityWithType: string;
  location: string;
  onDecide: (isInterested: boolean | null) => Promise<void>;
};

/**
 * The card's Apply / Not interested / Share row. When the job already has a
 * decision (e.g. in search results), the matching button shows as pressed and
 * pressing it again clears the decision. An applied external job also gets a
 * button back to its application.
 */
export function JobPostProviderActions({
  job,
  facilityWithType,
  location,
  onDecide,
}: JobPostProviderActionsProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [showExternalDialog, setShowExternalDialog] = useState(false);

  const isApplied = job.decision?.interested === true;
  const isNotInterested = job.decision?.interested === false;

  const handleDecide = async (isInterested: boolean) => {
    const isUndo = job.decision?.interested === isInterested;
    setIsSaving(true);
    try {
      await onDecide(isUndo ? null : isInterested);
    } finally {
      setIsSaving(false);
    }
  };

  // One row, Apply first and widest so the card leads toward applying.
  // Not interested is a quieter outline button; on phones it shrinks to an
  // icon so Apply keeps most of the width.
  return (
    <div className="space-y-3 border-t border-slate-200 pt-4">
      <div className="flex items-center gap-2 sm:gap-3">
        <Button
          disabled={isSaving}
          aria-pressed={isApplied}
          onClick={() => handleDecide(true)}
          className={cn(
            "flex-1 gap-2 border px-3",
            isApplied
              ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
              : "border-primary",
          )}
        >
          <ThumbsUp className={cn("h-4 w-4", isApplied && "fill-current")} />
          {isApplied ? "Applied" : "Apply"}
        </Button>
        <Button
          variant="outline"
          disabled={isSaving}
          aria-pressed={isNotInterested}
          aria-label="Not interested"
          onClick={() => handleDecide(false)}
          className={cn(
            "h-12 w-12 shrink-0 gap-2 px-0 text-slate-600 sm:w-auto sm:px-4",
            isNotInterested &&
              "border-slate-800 bg-slate-800 text-white hover:bg-slate-700 hover:text-white",
          )}
        >
          <ThumbsDown
            className={cn("h-4 w-4", isNotInterested && "fill-current")}
          />
          <span className="hidden sm:inline">Not interested</span>
        </Button>
        {job.id && (
          <JobShareButton
            jobId={job.id}
            jobTitle={job.title}
            facilityWithType={facilityWithType}
            location={location}
            className="h-12 w-12 shrink-0 px-0"
          />
        )}
      </div>
      {job.id && job.externalUrl && isApplied && (
        <Button
          variant="outline"
          onClick={() => setShowExternalDialog(true)}
          className="w-full gap-2 px-3"
        >
          <ExternalLink className="h-4 w-4" />
          View application
        </Button>
      )}
      {job.id && job.externalUrl && showExternalDialog && (
        <JobPostExternalAcceptDialog
          jobId={job.id}
          eligibility={job.eligibility}
          externalUrl={job.externalUrl}
          onClose={() => setShowExternalDialog(false)}
          onSuccess={() => {}}
          alreadyLiked={true}
        />
      )}
    </div>
  );
}
